import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar,
  Home,
  Scale,
  FileText,
  Plane,
} from "lucide-react";
import SampleBadge from "../ui/SampleBadge";
import { formatTime } from "./utils";

interface ScheduleWidgetProps {
  events: any[];
}

const getEventIcon = (type: string) => {
  switch (type) {
    case "closing":
      return Home;
    case "hearing":
      return Scale;
    case "filing":
      return FileText;
    case "immigration":
      return Plane;
    case "court":
      return Scale;
    default:
      return Calendar;
  }
};

const ScheduleWidget: React.FC<ScheduleWidgetProps> = ({ events }) => {
  const navigate = useNavigate();

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-slate-700" aria-hidden="true" />
          <h2 className="text-lg font-bold" style={{ color: "#1F2937" }}>
            Today's Schedule
          </h2>
        </div>
        <span className="text-xs font-medium text-slate-500">
          {new Date().toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })}
        </span>
      </div>

      {!events || events.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="h-12 w-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
            <Calendar className="h-6 w-6 text-slate-300" />
          </div>
          <p className="font-medium text-slate-500 text-sm">No events today</p>
          <p className="text-xs text-slate-400">Your schedule is clear</p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((event: any) => {
            const Icon = getEventIcon(event.type);
            return (
              <div
                key={event._id}
                className="flex gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                onClick={() => navigate("/calendar")}
              >
                <div className="flex flex-col items-center flex-shrink-0 w-16">
                  <span
                    className="text-xs font-semibold"
                    style={{ color: "#1F2937" }}
                  >
                    {formatTime(event.startAt)}
                  </span>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 mb-1">
                    <Icon
                      className="h-4 w-4 text-slate-600 mt-0.5 flex-shrink-0"
                      aria-hidden="true"
                    />
                    <div className="flex items-center gap-2">
                      <p
                        className="font-medium text-sm"
                        style={{ color: "#1F2937" }}
                      >
                        {event.title}
                      </p>
                      {event.isSample && <SampleBadge />}
                    </div>
                  </div>
                  <p className="text-xs text-slate-600 truncate">
                    {event.matterId?.title || ""}
                  </p>
                  {event.location && (
                    <p className="text-xs text-slate-500 mt-0.5">
                      {event.location}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </motion.section>
  );
};

export default ScheduleWidget;
